import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CharacterCountBar } from "./CharacterCountBar";
import { ALT_TEXT_LIMITS } from "./seoCharLimits";
import type {
	PageIssue,
	AltTextFix,
	AltTextDetails,
} from "@/types/technicalAudit";
import { ImageOff, Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { technicalAuditService } from "@/services/technicalAuditService";

interface ImageAltDiffCardProps {
	issue: PageIssue;
	onEdit: (value: AltTextFix) => void;
}

const readAltTextValue = (value: unknown): string => {
	if (!value) return "";
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) return "";
		try {
			return readAltTextValue(JSON.parse(trimmed));
		} catch {
			return trimmed;
		}
	}
	if (typeof value === "object") {
		const data = value as Record<string, unknown>;
		const alt =
			data.new_alt_text ||
			data.newAltText ||
			data.alt_text ||
			data.altText ||
			data.suggested_alt_text ||
			data.suggestedAltText ||
			data.generated_alt_text ||
			data.generatedAltText ||
			data.recommended_alt_text ||
			data.recommendedAltText;
		return typeof alt === "string" ? alt : "";
	}
	return "";
};

const readProposedAltText = (issue: PageIssue): string =>
		readAltTextValue(issue.approved_value) ||
		readAltTextValue(issue.proposed_fix) ||
		readAltTextValue(issue.proposed_value);

const buildFilenameHint = (issue: PageIssue, details: AltTextDetails): string => {
	const source = details.image_filename || details.image_url || issue.page_url;
	const filename = source.split("/").pop()?.split("?")[0] || "";
	const stem = filename.replace(/\.[^.]+$/, "");
	return stem
		.replace(/[-_]+/g, " ")
		.replace(/\b\d{2,5}x\d{2,5}\b/gi, " ")
		.replace(/\b24\s+7\b/g, "24/7")
		.replace(/\s+/g, " ")
		.trim();
};

export function ImageAltDiffCard({ issue, onEdit }: ImageAltDiffCardProps) {
	const details = issue.issue_details as AltTextDetails;
	const filenameHint = buildFilenameHint(issue, details);
	const proposedAltText = readProposedAltText(issue);

	const [editedAlt, setEditedAlt] = useState(() => proposedAltText);
	const [imageError, setImageError] = useState(false);
	const [wpMediaAltText, setWpMediaAltText] = useState<string | null>(null);
	const [wpMediaLookupStatus, setWpMediaLookupStatus] = useState<string | null>(null);
	const [wpMediaLookupError, setWpMediaLookupError] = useState<string | null>(null);
	const [wpMediaId, setWpMediaId] = useState<string | number | null>(null);
	const [candidateImageUrl, setCandidateImageUrl] = useState<string | null>(null);
	const [regenLoading, setRegenLoading] = useState(false);
	const [regenError, setRegenError] = useState<string | null>(null);
	const [useCandidate, setUseCandidate] = useState(false);

	useEffect(() => {
		setEditedAlt(proposedAltText);
	}, [proposedAltText]);

	const handleRegenerateImage = async () => {
		setRegenLoading(true);
		setRegenError(null);
		setUseCandidate(false);
		try {
			const res = await technicalAuditService.regenerateImage(issue.id);
			setCandidateImageUrl(res.candidate_image_url);
		} catch (error) {
			setRegenError(
				error instanceof Error
					? error.message
					: "Could not generate a candidate image.",
			);
		} finally {
			setRegenLoading(false);
		}
	};

	useEffect(() => {
		let cancelled = false;
		if (issue.issue_type !== "alt_text") return;

		technicalAuditService
			.previewFix(issue.id)
			.then((preview) => {
				if (cancelled) return;
				const payload = preview.planned_payload || {};
				const mediaAlt =
					typeof payload.wordpress_media_alt_text === "string"
						? payload.wordpress_media_alt_text
						: null;
				setWpMediaAltText(mediaAlt);
				setWpMediaLookupStatus(
					typeof payload.wordpress_media_lookup_status === "string"
						? payload.wordpress_media_lookup_status
						: null,
				);
				setWpMediaLookupError(
					typeof payload.wordpress_media_lookup_error === "string"
						? payload.wordpress_media_lookup_error
						: null,
				);
				setWpMediaId(
					typeof payload.wordpress_media_id === "string" ||
						typeof payload.wordpress_media_id === "number"
						? payload.wordpress_media_id
						: null,
				);
			})
			.catch((error) => {
				if (cancelled) return;
				setWpMediaLookupStatus("failed");
				setWpMediaLookupError(
					error instanceof Error ? error.message : "Could not check WordPress media alt text.",
				);
			});

		return () => {
			cancelled = true;
		};
	}, [issue.id, issue.issue_type, issue.approved_value]);

	// Update parent when edited value changes. When the operator has chosen the
	// regenerated candidate, include replacement_image_url so Apply swaps the image.
	useEffect(() => {
		onEdit({
			new_alt_text: editedAlt,
			...(useCandidate && candidateImageUrl
				? { replacement_image_url: candidateImageUrl }
				: {}),
		});
	}, [editedAlt, onEdit, useCandidate, candidateImageUrl]);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Image Alt Text</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* Image Preview — large, object-contain (no crop) so distortions in
				    AI-generated images are visible; click to open at full resolution. */}
				<div className="flex flex-col gap-4 sm:flex-row">
					<div className="flex-shrink-0">
						{imageError ? (
							<div className="flex h-44 w-44 items-center justify-center rounded-md border bg-muted md:h-56 md:w-56">
								<ImageOff className="h-8 w-8 text-muted-foreground" />
							</div>
						) : (
							<a
								href={details.image_url}
								target="_blank"
								rel="noopener noreferrer"
								title="Open full-size image in a new tab"
								className="block"
							>
								<img
									src={details.image_url}
									alt={details.current_alt_text || "Preview"}
									className="h-44 w-44 rounded-md border bg-muted object-contain transition hover:opacity-90 md:h-56 md:w-56"
									onError={() => setImageError(true)}
									loading="lazy"
								/>
							</a>
						)}
					</div>

					<div className="flex-1 space-y-2">
						<div>
							<div className="text-xs font-medium text-muted-foreground">
								Image URL
							</div>
							<p className="text-xs break-all text-muted-foreground">
								{details.image_url}
							</p>
						</div>
						{details.image_filename && (
							<div>
								<div className="text-xs font-medium text-muted-foreground">
									Filename
								</div>
								<p className="text-xs text-muted-foreground">
									{details.image_filename}
								</p>
							</div>
						)}
						{details.image_type && (
							<div>
								<div className="text-xs font-medium text-muted-foreground">
									Type
								</div>
								<p className="text-xs text-muted-foreground">
									{details.image_type}
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Regenerate image (preview-only): the source image may be distorted
				    AI art — generate a candidate replacement to compare. NOT applied. */}
				<div className="rounded-md border border-dashed border-border p-3">
					<div className="flex items-center justify-between gap-3">
						<div>
							<div className="text-sm font-medium text-foreground">
								Replacement image
							</div>
							<p className="text-xs text-muted-foreground">
								Source image looks off? Generate a candidate (preview
								only — not applied to WordPress).
							</p>
						</div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={handleRegenerateImage}
							disabled={regenLoading}
							className="shrink-0 gap-1.5"
						>
							{regenLoading ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<Wand2 className="h-3.5 w-3.5" />
							)}
							{regenLoading
								? "Generating…"
								: candidateImageUrl
									? "Regenerate image"
									: "Generate image"}
						</Button>
					</div>
					{regenError && (
						<p className="mt-2 text-xs text-destructive">{regenError}</p>
					)}
					{candidateImageUrl && (
						<div className="mt-3">
							<div className="mb-1 text-xs font-medium text-muted-foreground">
								{useCandidate
									? "Selected — will replace the image on Apply"
									: "Candidate (review it, then choose to use it)"}
							</div>
							<a
								href={candidateImageUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="block"
								title="Open candidate at full size"
							>
								<img
									src={candidateImageUrl}
									alt="Candidate replacement"
									className={`h-44 w-full max-w-sm rounded-md border bg-muted object-contain transition hover:opacity-90 md:h-56 ${
										useCandidate ? "ring-2 ring-coral" : ""
									}`}
								/>
							</a>
							<div className="mt-2 flex items-center gap-2">
								<Button
									type="button"
									variant={useCandidate ? "default" : "outline"}
									size="sm"
									onClick={() => setUseCandidate((v) => !v)}
									className={useCandidate ? "bg-coral hover:bg-coral-hover" : ""}
								>
									{useCandidate ? "Using this image" : "Use this image"}
								</Button>
								{useCandidate && (
									<span className="text-xs text-muted-foreground">
										On Approve + Apply, this uploads to WordPress and
										repoints the page image.
									</span>
								)}
							</div>
						</div>
					)}
				</div>

				{/* Current Alt Text */}
				<div>
					<div className="mb-2 text-sm font-medium text-muted-foreground">
						Crawl / Rendered Alt Text
					</div>
					<div className="rounded-md border bg-muted/50 p-3">
						<p className="text-sm">
							{details.current_alt_text || (
								<span className="italic text-muted-foreground">(empty)</span>
							)}
						</p>
					</div>
				</div>

				<div>
					<div className="mb-2 text-sm font-medium text-muted-foreground">
						WordPress Media Alt Text
					</div>
					<div className="rounded-md border bg-muted/50 p-3">
						<p className="text-sm">
							{wpMediaLookupStatus === null ? (
								<span className="italic text-muted-foreground">Checking WordPress media...</span>
							) : wpMediaAltText ? (
								wpMediaAltText
							) : wpMediaLookupError ? (
								<span className="italic text-red-600 dark:text-red-400">{wpMediaLookupError}</span>
							) : (
								<span className="italic text-muted-foreground">(empty)</span>
							)}
						</p>
						{wpMediaId && (
							<p className="mt-2 text-xs text-muted-foreground">
								Media ID: {wpMediaId}
							</p>
						)}
					</div>
					{wpMediaAltText && !details.current_alt_text && (
						<p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
							WordPress already has media alt text. If a crawl still reports this issue, the rendered page HTML may need a separate page-content fix.
						</p>
					)}
				</div>

				{/* Proposed Alt Text */}
				<div>
					<div className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
						Proposed Alt Text
					</div>
					<Textarea
						value={editedAlt}
						onChange={(e) => setEditedAlt(e.target.value)}
						className="min-h-[80px]"
						placeholder="Generate a fix or enter the alt text to apply..."
					/>
					<div className="mt-2">
						<CharacterCountBar
							current={editedAlt.length}
							min={ALT_TEXT_LIMITS.min}
							max={ALT_TEXT_LIMITS.max}
							optimal={ALT_TEXT_LIMITS.optimal}
						/>
					</div>
					{!editedAlt && filenameHint && (
						<p className="mt-2 text-xs text-muted-foreground">
							Filename hint: {filenameHint}
						</p>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
